import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { useI18n } from "@/context/I18nContext";
import { Check, X, ChevronDown, Star, Mic, Video, MessageSquare, PhoneOff } from "lucide-react";

const Badge = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-400">{children}</span>
);

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`py-20 px-6 border-t border-slate-800/60 ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
);

export default function Landing() {
  const { t } = useI18n();

  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <Navbar />

      {/* HERO */}
      <section className="mesh-bg grid-overlay pt-36 pb-20 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Badge>{t("landing.badge")}</Badge>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-white">{t("landing.h1")}</h1>
            <p className="mt-6 text-lg text-slate-400 leading-relaxed">{t("landing.h1_sub")}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-cta-register" className="px-7 py-3.5 rounded-full bg-indigo-600 text-white font-semibold hover:bg-indigo-500 transition-colors">
                {t("landing.cta_main")}
              </Link>
              <a href="#jak-to-funguje" data-testid="hero-cta-how" className="px-7 py-3.5 rounded-full border border-slate-700 text-slate-200 font-semibold hover:border-indigo-500 hover:text-white transition-colors">
                {t("landing.cta_how")}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              {[t("landing.chip1"), t("landing.chip2"), t("landing.chip3")].map((c) => (
                <span key={c} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#131B2A] border border-slate-700 text-slate-300">{c}</span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="bg-[#131B2A] border border-slate-800 rounded-2xl p-6 shadow-2xl" data-testid="hero-demo-card">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">🟢 {t("landing.demo_status")}</span>
              <span className="flex items-center gap-1.5 text-emerald-400 font-semibold text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 live-dot" /> {t("landing.demo_online")}
              </span>
            </div>
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-slate-400">👥 {t("landing.demo_meeting")}</span>
              <span className="text-white font-semibold">{t("landing.demo_participants")}</span>
            </div>
            <div className="mt-4 font-mono text-xs bg-indigo-950/80 text-indigo-300 border border-indigo-700/50 px-3 py-2 rounded-xl">
              🔒 digisidlo.cz/valmez/akstancik
            </div>
            <div className="mt-4 rounded-xl bg-[#0B0F17] border border-slate-800 p-5">
              <div className="flex gap-3">
                <span className="w-14 h-14 rounded-xl bg-indigo-600/30 border border-indigo-500/40 flex items-center justify-center font-bold text-indigo-300">AŠ</span>
                <span className="w-14 h-14 rounded-xl bg-violet-600/30 border border-violet-500/40 flex items-center justify-center font-bold text-violet-300">KN</span>
              </div>
              <p className="mt-3 text-xs text-slate-500">{t("landing.demo_running")}</p>
              <div className="mt-4 flex gap-2">
                {[Mic, Video, MessageSquare].map((I, i) => (
                  <span key={i} className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center"><I className="w-4 h-4 text-slate-300" /></span>
                ))}
                <span className="w-9 h-9 rounded-full bg-red-600/80 flex items-center justify-center"><PhoneOff className="w-4 h-4 text-white" /></span>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="max-w-7xl mx-auto mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-[#131B2A]/60 border border-slate-800 rounded-2xl p-6 text-center">
              <div className="font-mono text-2xl font-semibold text-white">{t(`landing.stat${i}_v`)}</div>
              <div className="mt-1 text-xs text-slate-500">{t(`landing.stat${i}_l`)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CO JE DIGISIDLO */}
      <Section id="co-je">
        <Badge>{t("landing.whatis_badge")}</Badge>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{t("landing.whatis_h")}</h2>
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-5 text-slate-300 leading-relaxed">
            <p className="font-semibold text-white">{t("landing.whatis_p1")}</p>
            <div className="font-mono text-sm bg-indigo-950/80 text-indigo-300 border border-indigo-700/50 px-4 py-2.5 rounded-xl inline-block">digisidlo.cz/valmez/akstancik</div>
            <p>{t("landing.whatis_p2")}</p>
            <p className="font-semibold text-white">{t("landing.whatis_p3")}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">{t("landing.whatis_imagine")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="card-hover bg-[#131B2A] border border-slate-800 rounded-2xl p-6">
                  <h4 className="font-semibold text-white text-sm">{t(`landing.metaphor${i}_t`)}</h4>
                  <p className="mt-2 text-xs text-slate-400 leading-relaxed">{t(`landing.metaphor${i}_d`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* SROVNANI */}
      <Section id="rozdil">
        <Badge>{t("landing.vs_badge")}</Badge>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-2xl">{t("landing.vs_h")}</h2>
        <p className="mt-4 text-slate-400 max-w-2xl">{t("landing.vs_sub")}</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-red-900/40 bg-red-950/10 p-8">
            <h3 className="text-lg font-bold text-white">{t("landing.vs_them_h")}</h3>
            <p className="text-xs text-slate-500 mt-1">{t("landing.vs_them_sub")}</p>
            <ul className="mt-6 space-y-3">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-400"><X className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />{t(`landing.vs_them_${i}`)}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-emerald-700/40 bg-emerald-950/10 p-8 relative">
            <span className="absolute -top-3 left-6 text-xs font-semibold px-3 py-1 rounded-full bg-violet-600 text-white">{t("landing.vs_us_badge")}</span>
            <h3 className="text-lg font-bold text-white">{t("landing.vs_us_h")}</h3>
            <p className="text-xs text-slate-500 mt-1">{t("landing.vs_us_sub")}</p>
            <ul className="mt-6 space-y-3">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-200"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />{t(`landing.vs_us_${i}`)}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* JAK TO FUNGUJE */}
      <Section id="jak-to-funguje">
        <Badge>{t("landing.how_badge")}</Badge>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{t("landing.how_h")}</h2>
        <p className="mt-4 text-slate-400 max-w-2xl">{t("landing.how_sub")}</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card-hover bg-[#131B2A] border border-slate-800 rounded-2xl p-7">
              <span className="w-9 h-9 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center font-mono">{i}</span>
              <h3 className="mt-5 font-semibold text-white">{t(`landing.how${i}_t`)}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{t(`landing.how${i}_d`)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* PRISTUP */}
      <Section id="pristup">
        <Badge>{t("landing.access_badge")}</Badge>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{t("landing.access_h")}</h2>
        <p className="mt-4 text-slate-400 max-w-2xl">{t("landing.access_sub")}</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {["pub", "priv"].map((k) => (
            <div key={k} className="card-hover bg-[#131B2A] border border-slate-800 rounded-2xl p-8" data-testid={`access-card-${k}`}>
              <h3 className="text-xl font-bold text-white">{t(`landing.${k}_h`)}</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{t(`landing.${k}_d`)}</p>
              <h4 className="mt-6 text-xs font-semibold uppercase tracking-widest text-indigo-400">{t(`landing.${k}_for`)}</h4>
              <div className="mt-3 flex flex-wrap gap-2">
                {t(`landing.${k}_tags`).split("|").map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1.5 rounded-full bg-[#0B0F17] border border-slate-700 text-slate-300">{tag}</span>
                ))}
              </div>
              <h4 className="mt-6 text-xs font-semibold uppercase tracking-widest text-indigo-400">{t(`landing.${k}_how_h`)}</h4>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{t(`landing.${k}_how`)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* PRO KOHO */}
      <Section id="pro-koho">
        <Badge>{t("landing.who_badge")}</Badge>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-2xl">{t("landing.who_h")}</h2>
        <p className="mt-4 text-slate-400 max-w-2xl">{t("landing.who_sub")}</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card-hover bg-[#131B2A] border border-slate-800 rounded-2xl p-7">
              <h3 className="font-semibold text-white">{t(`landing.who${i}_t`)}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{t(`landing.who${i}_d`)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* FUNKCE */}
      <Section id="funkce">
        <Badge>{t("landing.feat_badge")}</Badge>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{t("landing.feat_h")}</h2>
        <p className="mt-4 text-slate-400 max-w-2xl">{t("landing.feat_sub")}</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="card-hover bg-[#131B2A] border border-slate-800 rounded-2xl p-6" data-testid={`feature-card-${i}`}>
              <h3 className="font-semibold text-white text-sm">{["📹", "💬", "🖥️", "📎", "👥", "⏳", "🎨", "🟢"][i - 1]} {t(`landing.feat${i}_t`)}</h3>
              <p className="mt-2 text-xs text-slate-400 leading-relaxed">{t(`landing.feat${i}_d`)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* PRO FIRMY */}
      <Section id="pro-firmy">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge>{t("landing.firm_badge")}</Badge>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{t("landing.firm_h")}</h2>
            <p className="mt-5 text-slate-300 leading-relaxed">{t("landing.firm_p1")}</p>
            <p className="mt-3 text-slate-400 leading-relaxed">{t("landing.firm_p2")}</p>
            <ul className="mt-6 space-y-2.5">
              {t("landing.firm_list").split("|").map((li) => (
                <li key={li} className="flex gap-3 text-sm text-slate-200"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />{li}</li>
              ))}
            </ul>
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={`flex items-center justify-between bg-[#131B2A] border border-slate-800 rounded-xl px-6 py-4 ${i > 1 ? "ml-8" : ""}`}>
                <span className="font-semibold text-white text-sm">{t(`landing.firm_ex${i}_t`)}</span>
                <span className="font-mono text-xs text-indigo-300">{t(`landing.firm_ex${i}_a`)}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* PRINOSY */}
      <Section id="prinosy">
        <Badge>{t("landing.benefit_badge")}</Badge>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{t("landing.benefit_h")}</h2>
        <p className="mt-4 text-slate-400 max-w-2xl">{t("landing.benefit_sub")}</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="card-hover bg-[#131B2A] border border-slate-800 rounded-2xl p-6">
              <h3 className="font-semibold text-white text-sm">{t(`landing.ben${i}_t`)}</h3>
              <p className="mt-2 text-xs text-slate-400 leading-relaxed">{t(`landing.ben${i}_d`)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* PRIKLADY */}
      <Section id="priklady">
        <Badge>{t("landing.ex_badge")}</Badge>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{t("landing.ex_h")}</h2>
        <p className="mt-4 text-slate-400 max-w-2xl">{t("landing.ex_sub")}</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card-hover bg-[#131B2A] border border-slate-800 rounded-2xl p-8">
              <h3 className="font-semibold text-white">{t(`landing.ex${i}_t`)}</h3>
              <div className="mt-3 font-mono text-xs bg-indigo-950/80 text-indigo-300 border border-indigo-700/50 px-3 py-1.5 rounded-full inline-block">🔗 {t(`landing.ex${i}_a`)}</div>
              <p className="mt-4 text-sm text-slate-400 leading-relaxed">{t(`landing.ex${i}_d`)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* OHLASY */}
      <Section id="ohlasy">
        <Badge>{t("landing.test_badge")}</Badge>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{t("landing.test_h")}</h2>
        <p className="mt-4 text-slate-400 max-w-2xl">{t("landing.test_sub")}</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-[#131B2A] border border-slate-800 rounded-2xl p-8">
              <div className="flex gap-1">{[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
              <p className="mt-4 text-sm text-slate-300 leading-relaxed">{t(`landing.test${i}_q`)}</p>
              <div className="mt-6 pt-4 border-t border-slate-800">
                <div className="font-semibold text-white text-sm">{t(`landing.test${i}_n`)}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t(`landing.test${i}_r`)}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* CENIK */}
      <Section id="cenik">
        <Badge>{t("landing.price_badge")}</Badge>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{t("landing.price_h")}</h2>
        <p className="mt-4 text-slate-400 max-w-2xl">{t("landing.price_sub")}</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} data-testid={`pricing-plan-${i}`} className={`rounded-2xl p-8 border card-hover relative ${i === 2 ? "bg-indigo-950/40 border-indigo-500/50" : "bg-[#131B2A] border-slate-800"}`}>
              {i === 2 && <span className="absolute -top-3 left-6 text-xs font-semibold px-3 py-1 rounded-full bg-indigo-600 text-white">{t("landing.plan2_badge")}</span>}
              <h3 className="text-xl font-semibold text-white">{t(`landing.plan${i}_n`)}</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-mono text-3xl font-semibold text-white">{t(`landing.plan${i}_p`)}</span>
                <span className="text-sm text-slate-500">{t(`landing.plan${i}_per`)}</span>
              </div>
              <p className="mt-2 text-sm text-slate-400">{t(`landing.plan${i}_d`)}</p>
              <ul className="mt-6 space-y-2.5">
                {t(`landing.plan${i}_f`).split("|").map((f) => (
                  <li key={f} className="flex gap-2.5 text-sm text-slate-300"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              <Link to="/register" data-testid={`plan-cta-${i}`}
                className={`mt-8 block text-center px-5 py-2.5 rounded-full font-semibold text-sm transition-colors ${i === 2 ? "bg-indigo-600 text-white hover:bg-indigo-500" : "border border-slate-700 text-slate-200 hover:border-indigo-500"}`}>
                {t(`landing.plan${i}_cta`)}
              </Link>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-slate-400 text-center">{t("landing.price_note")}</p>
        <p className="mt-2 text-sm text-slate-500 text-center">{t("landing.price_cancel")}</p>
        <div className="mt-10 max-w-3xl mx-auto bg-emerald-950/20 border border-emerald-700/40 rounded-2xl p-8 text-center">
          <h3 className="text-lg font-bold text-white">{t("landing.guarantee_h")}</h3>
          <p className="mt-3 text-sm text-slate-300 leading-relaxed">{t("landing.guarantee_d")}</p>
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq">
        <Badge>{t("landing.faq_badge")}</Badge>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{t("landing.faq_h")}</h2>
        <p className="mt-4 text-slate-400 max-w-2xl">{t("landing.faq_sub")}</p>
        <div className="mt-10 max-w-3xl space-y-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
            <details key={i} className="group bg-[#131B2A] border border-slate-800 rounded-xl" data-testid={`faq-item-${i}`}>
              <summary className="flex items-center justify-between gap-4 px-6 py-4 cursor-pointer text-sm font-semibold text-white list-none">
                {t(`landing.faq${i}_q`)}
                <ChevronDown className="w-4 h-4 text-slate-500 group-open:rotate-180 transition-transform shrink-0" />
              </summary>
              <p className="px-6 pb-5 text-sm text-slate-400 leading-relaxed">{t(`landing.faq${i}_a`)}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section id="registrace" className="text-center">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white max-w-2xl mx-auto">{t("landing.cta_h")}</h2>
        <p className="mt-5 text-slate-400 max-w-xl mx-auto">{t("landing.cta_sub")}</p>
        <Link to="/register" data-testid="footer-cta-register"
          className="mt-9 inline-block px-9 py-4 rounded-full bg-indigo-600 text-white font-semibold text-lg hover:bg-indigo-500 transition-colors">
          {t("landing.cta_btn")}
        </Link>
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {t("landing.cta_chips").split("|").map((c) => (
            <span key={c} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#131B2A] border border-slate-700 text-slate-300">{c}</span>
          ))}
        </div>
      </Section>

      <footer className="border-t border-slate-800/60 py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-heading font-bold text-white">Digisídlo</span>
          <span className="text-sm text-slate-500">{t("landing.footer")}</span>
        </div>
      </footer>
    </div>
  );
}
